import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { invalidateAdminInboxCounts } from '@/hooks/useAdminInboxCounts';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ChevronLeft, ChevronRight, Clock, MapPin, User, Phone, CheckCircle2, AlertTriangle, Pencil, X, Repeat } from 'lucide-react';
import { format, addDays, isSameDay, parseISO, getDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { PersonAvatar, StatusChip, adminButton, getStatusMeta } from '@/components/admin/ui';
import { EditScopeDialog } from '@/components/admin/recurring/EditScopeDialog';
import { RepeatVisitPanel } from '@/components/admin/recurring/RepeatVisitPanel';
import { futureEditSummary, visitChangesFromForm } from '@/components/admin/recurring/visit-edit';
import { invalidateRecurring, updateSeriesFromVisit } from '@/hooks/useRecurringSeries';
import type { EditScope } from '@/lib/recurrence';
import { JobWorkPanel } from '@/components/admin/jobs/JobWorkPanel';
import { ChecklistProgressBadge } from '@/components/admin/jobs/ChecklistProgressBadge';
import { useChecklistProgress } from '@/hooks/useChecklists';
import { useScheduleClashCheck } from '@/hooks/useScheduleClashCheck';
import { ScheduleClashDialog } from '@/components/admin/schedule/ScheduleClashDialog';

interface TeamMember {
  id: string;
  name: string;
  email: string;
}

interface Service {
  id: string;
  name: string;
  // Cleaners the service inherently needs; authoritative fallback when a job's
  // own required_cleaners is missing (older pending booking requests).
  resource_required?: number | null;
}

interface JobAssignment {
  team_member_id: string;
  team_members?: TeamMember | null;
}

interface Job {
  id: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  team_member_id: string | null;
  service_id: string | null;
  status: string;
  notes: string | null;
  required_cleaners?: number | null;
  team_members?: TeamMember;
  services?: Service;
  job_assignments?: JobAssignment[];
  // Repeat cleans: the series this visit belongs to, its pattern date, and
  // whether it was changed by hand ("this visit only").
  series_id?: string | null;
  series_occurrence_date?: string | null;
  is_exception?: boolean | null;
}

// Resolve the set of cleaners assigned to a job. The new job_assignments table
// is the source of truth; we fall back to the legacy single team_member_id so
// jobs created before multi-cleaner support still render correctly.
const getAssignedMemberIds = (job: Job): string[] => {
  const ids = (job.job_assignments ?? [])
    .map((a) => a.team_member_id)
    .filter((id): id is string => Boolean(id));
  if (ids.length === 0 && job.team_member_id) return [job.team_member_id];
  return Array.from(new Set(ids));
};

// Cleaners a job truly requires: its own required_cleaners OR the linked
// service's resource_required, whichever is larger and valid. Pending requests
// created before required_cleaners was persisted fall back to the service's
// value (issue #49). Always at least 1.
const getRequiredCleaners = (job: {
  required_cleaners?: number | null;
  services?: { resource_required?: number | null } | null;
}): number => {
  const toValid = (value: unknown): number => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
  };
  return Math.max(1, toValid(job.required_cleaners), toValid(job.services?.resource_required));
};

interface BusinessHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 6am to 7pm

// Status legend swatches. These are a static colour key on the dispatch grid —
// cross-date category browsing now lives in the Schedule Overview filters above
// the calendar (issue #49), so the grid stays a focused single-day view. The
// "unassigned" key is still honoured for the dashboard deep-link, which collapses
// the grid to the Unassigned column for the linked day. Keys match
// `jobs.status` values (except 'unassigned').
const STATUS_FILTERS = [
  { key: 'scheduled', label: 'Scheduled', dot: 'bg-amber-400 border-amber-500' },
  { key: 'in_progress', label: 'In Progress', dot: 'bg-sky-400 border-sky-500' },
  { key: 'completed', label: 'Completed', dot: 'bg-emerald-400 border-emerald-500' },
  { key: 'unassigned', label: 'Unassigned', dot: 'bg-gray-300 border-gray-400' },
] as const;

const VALID_FILTERS = new Set(STATUS_FILTERS.map((f) => f.key));

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const maybeError = error as { message?: unknown; error_description?: unknown; details?: unknown; code?: unknown };
    if (typeof maybeError.message === 'string' && maybeError.message.trim()) return maybeError.message;
    if (typeof maybeError.error_description === 'string' && maybeError.error_description.trim()) return maybeError.error_description;
    if (typeof maybeError.details === 'string' && maybeError.details.trim()) return maybeError.details;
    if (typeof maybeError.code === 'string' && maybeError.code.trim()) return maybeError.code;
  }
  return 'Unable to load schedule data. Please try again.';
};

interface DispatchCalendarProps {
  selectedMemberId?: string | null;
  /**
   * View-only mode for cleaners: hides the admin-only assignment/status/notes/
   * edit/complete controls in the job dialog so a cleaner can read their own
   * job details without mutating them.
   */
  readOnly?: boolean;
  /** Deep-link day (yyyy-MM-dd) — e.g. the dashboard unassigned alert. */
  initialDate?: string | null;
  /** Deep-link status/unassigned filter to activate on mount. */
  initialFilter?: string | null;
  /** Deep-link job id to auto-open (when visible on the displayed day). */
  focusJobId?: string | null;
}

const DispatchCalendar = ({
  selectedMemberId,
  readOnly = false,
  initialDate = null,
  initialFilter = null,
  focusJobId = null,
}: DispatchCalendarProps) => {
  const { toast } = useToast();
  const { orgId, loading: orgLoading } = useAdminOrg();
  const queryClient = useQueryClient();
  // Shared double-booking / working-hours prompt ("Book anyway" / "Pick another time").
  const { confirmSchedule, clashPrompt } = useScheduleClashCheck(orgId);
  const [loading, setLoading] = useState(true);
  const hasLoadedDataRef = useRef(false);
  const [currentDate, setCurrentDate] = useState(() => {
    if (initialDate) {
      const parsed = parseISO(initialDate);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  // Active status legend filter. Cleaners (readOnly) never get a filter — they
  // already see only their own scoped column.
  const [activeFilter, setActiveFilter] = useState<string | null>(
    !readOnly && initialFilter && VALID_FILTERS.has(initialFilter) ? initialFilter : null
  );
  const [jobs, setJobs] = useState<Job[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editableNotes, setEditableNotes] = useState('');
  const [savingAction, setSavingAction] = useState<'complete' | 'save' | 'edit' | null>(null);
  const isSaving = savingAction !== null;
  // Pending selection in the "Add cleaner" control of the job dialog.
  const [addCleanerId, setAddCleanerId] = useState('');
  // Staged dialog edits. Status and cleaner add/remove changes update these
  // local drafts only; nothing is written to Supabase until the bottom-right
  // Save button persists them together (issue #44 follow-up). This keeps the
  // dialog a single, predictable commit point instead of autosaving on every
  // dropdown change.
  const [draftStatus, setDraftStatus] = useState('');
  const [draftAssignedIds, setDraftAssignedIds] = useState<string[]>([]);
  // Inline "Edit Job" mode: lets owners edit the scheduled job's core order
  // details (title/client/contact/address/date/time/service) from this dialog.
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    service_id: '',
  });
  // "This visit only" vs "this and all future visits" prompt for repeat cleans.
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeBusy, setScopeBusy] = useState<EditScope | null>(null);

  // Live clock for the red "now" line (minute resolution).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);

  const displayedMembers = useMemo(() => {
    const unassignedColumn = { id: 'unassigned', name: 'Unassigned', email: '' };

    // The Unassigned filter collapses the grid to just the Unassigned column so
    // the owner can focus the jobs that still need a cleaner.
    if (activeFilter === 'unassigned') {
      return [unassignedColumn];
    }
    if (selectedMemberId) {
      const member = teamMembers.find(m => m.id === selectedMemberId);
      return member ? [member] : [];
    }
    // Include "Unassigned" column
    return [unassignedColumn, ...teamMembers];
  }, [teamMembers, selectedMemberId, activeFilter]);

  // Detect jobs that double-book a cleaner for the same date/time. Mirrors the
  // Week view (ScheduleAdmin) so the Team tab flags conflicts too. Assignees are
  // resolved through getAssignedMemberIds so job_assignments are authoritative —
  // a stale legacy team_member_id can no longer mark jobs with different real
  // assignees as clashing, nor hide a conflict between two jobs that share a real
  // assignee only via job_assignments (issue #49). This matches how
  // getJobsForMember places jobs into columns, so every block that visually
  // stacks in a column is also correctly flagged.
  const overlappingJobIds = useMemo(() => {
    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const conflicting = new Set<string>();
    const candidates = jobs
      .filter((job) => job.status !== 'cancelled')
      .map((job) => ({ job, members: new Set(getAssignedMemberIds(job)) }))
      .filter((c) => c.members.size > 0);

    for (let i = 0; i < candidates.length; i++) {
      for (let j = i + 1; j < candidates.length; j++) {
        const a = candidates[i];
        const b = candidates[j];
        if (
          a.job.scheduled_date === b.job.scheduled_date &&
          toMinutes(a.job.start_time) < toMinutes(b.job.end_time) &&
          toMinutes(a.job.end_time) > toMinutes(b.job.start_time) &&
          [...a.members].some((id) => b.members.has(id))
        ) {
          conflicting.add(a.job.id);
          conflicting.add(b.job.id);
        }
      }
    }

    return conflicting;
  }, [jobs]);

  const getBusinessHoursForDay = (date: Date): BusinessHour | undefined => {
    const dayOfWeek = getDay(date);
    return businessHours.find(h => h.day_of_week === dayOfWeek);
  };

  const isHourOpen = (date: Date, hour: number): boolean => {
    const bh = getBusinessHoursForDay(date);
    if (!bh || !bh.is_open) return false;
    
    const openHour = parseInt(bh.open_time.split(':')[0]);
    const closeHour = parseInt(bh.close_time.split(':')[0]);
    
    return hour >= openHour && hour < closeHour;
  };

  useEffect(() => {
    if (initialDate) {
      const parsed = parseISO(initialDate);
      if (!Number.isNaN(parsed.getTime())) {
        setCurrentDate(parsed);
      }
    }
  }, [initialDate]);

  useEffect(() => {
    if (readOnly) return;
    setActiveFilter(initialFilter && VALID_FILTERS.has(initialFilter) ? initialFilter : null);
  }, [initialFilter, readOnly]);

  // Auto-open a deep-linked job's dialog once it has loaded for the displayed
  // day (e.g. the dashboard unassigned alert). Guarded for cleaners so a job id
  // can never surface another cleaner's / an unassigned job they must not see.
  const focusedJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !focusJobId || focusedJobRef.current === focusJobId) return;
    const job = jobs.find((j) => j.id === focusJobId);
    if (!job) return;
    if (readOnly && (!selectedMemberId || !getAssignedMemberIds(job).includes(selectedMemberId))) return;
    focusedJobRef.current = focusJobId;
    openJobDialog(job);
  }, [loading, focusJobId, jobs, readOnly, selectedMemberId]);

  const fetchData = useCallback(async (): Promise<Job[]> => {
    const showFullPageLoader = !hasLoadedDataRef.current;
    if (showFullPageLoader) setLoading(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');

      // The dispatch grid is always a focused single-day view (issue #49): it
      // loads only the displayed day's jobs so it stays easy to navigate
      // day-by-day. Cross-date category browsing lives in the Schedule Overview
      // filters above the calendar, not here.
      const scopedJobsQuery = supabase
        .from('jobs')
        .select('*, team_members(id, name, email), services(id, name, resource_required), job_assignments(team_member_id, team_members(id, name, email))')
        .eq('organization_id', orgId)
        .eq('scheduled_date', dateStr)
        .order('start_time');

      const [jobsRes, teamRes, servicesRes, hoursRes] = await Promise.all([
        scopedJobsQuery,
        supabase.from('team_members').select('id, name, email').eq('organization_id', orgId).order('name'),
        supabase.from('services').select('id, name').eq('organization_id', orgId).eq('is_active', true).order('name'),
        supabase.from('business_hours').select('day_of_week, is_open, open_time, close_time').eq('organization_id', orgId),
      ]);

      if (jobsRes.error) throw jobsRes.error;
      if (teamRes.error) throw teamRes.error;
      if (servicesRes.error) throw servicesRes.error;
      if (hoursRes.error) throw hoursRes.error;

      const jobsData = (jobsRes.data || []) as Job[];
      setJobs(jobsData);
      // Keep the top-bar bell / sidebar counts in step with request approvals.
      invalidateAdminInboxCounts(queryClient);
      setTeamMembers(teamRes.data || []);
      setServices(servicesRes.data || []);
      setBusinessHours(hoursRes.data || []);
      return jobsData;
    } catch (error) {
      console.error('Failed to load dispatch calendar data', error);
      toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
      return [];
    } finally {
      hasLoadedDataRef.current = true;
      setLoading(false);
    }
  }, [currentDate, orgId, queryClient, toast]);

  // Refetch when the displayed day changes or the org resolves. The grid is
  // always a single-day view now (issue #49), so the loaded data set depends
  // only on the date — not on any category filter.
  useEffect(() => {
    if (orgLoading) return;
    if (!orgId) {
      hasLoadedDataRef.current = true;
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, orgId, orgLoading]);

  // Open the grid near the working day: around "now" when viewing today,
  // otherwise just before 8am. Purely a scroll position — no data impact.
  useEffect(() => {
    if (loading) return;
    const el = scrollRef.current;
    if (!el) return;
    const current = new Date();
    const bh = businessHours.find((h) => h.day_of_week === getDay(currentDate));
    const openMinutes = bh?.is_open ? parseInt(bh.open_time.split(':')[0], 10) * 60 : 8 * 60;
    let target = openMinutes - 30;
    // Keep "now" in view when it would fall below a ~7h window from opening.
    const nowMinutes = current.getHours() * 60 + current.getMinutes();
    if (isSameDay(currentDate, current) && nowMinutes > target + 7 * 60) target = nowMinutes - 180;
    el.scrollTop = Math.max(0, target - HOURS[0] * 60);
  }, [loading, currentDate, businessHours]);

  const handlePrevDay = () => setCurrentDate((prev) => addDays(prev, -1));
  const handleNextDay = () => setCurrentDate((prev) => addDays(prev, 1));
  const handleToday = () => setCurrentDate(new Date());

  const handleCompleteJob = async () => {
    if (readOnly) return;
    if (!selectedJob) return;
    setSavingAction('complete');
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'completed', notes: editableNotes || null })
        .eq('id', selectedJob.id);
      if (error) throw error;
      toast({ title: 'Job Completed', description: 'Job marked as completed successfully' });
      fetchData();
      setIsViewDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingAction(null);
    }
  };

  // Persist all staged dialog edits — notes, status, and cleaner add/remove —
  // in one Save. Cleaner removals/additions are written to job_assignments,
  // then notes/status (and the legacy primary cleaner column, if it moved) land
  // in a single jobs PATCH so the changes commit together. Only after the writes
  // succeed do we notify the newly-added cleaners and refresh the calendar,
  // keeping the dialog open and pointed at the fresh job so it stays usable.
  const handleSaveJob = async () => {
    if (readOnly) return;
    if (!selectedJob) return;
    const jobId = selectedJob.id;
    const originalIds = getAssignedMemberIds(selectedJob);
    // If the owner picked a cleaner in the dropdown and clicks Save without
    // pressing the smaller "Add cleaner" button, treat that pending selection as
    // part of the staged assignment. This makes Save the single commit action.
    const effectiveDraftAssignedIds =
      addCleanerId && !draftAssignedIds.includes(addCleanerId)
        ? [...draftAssignedIds, addCleanerId]
        : draftAssignedIds;
    const addedIds = effectiveDraftAssignedIds.filter((id) => !originalIds.includes(id));
    const removedIds = originalIds.filter((id) => !effectiveDraftAssignedIds.includes(id));

    setSavingAction('save');
    // Newly added cleaners: warn if any of them is already busy at this time.
    if (addedIds.length > 0) {
      const okToAssign = await confirmSchedule(
        {
          date: selectedJob.scheduled_date,
          start: selectedJob.start_time,
          end: selectedJob.end_time,
          memberIds: addedIds,
          excludeJobId: jobId,
        },
        { fallbackJobs: jobs, fallbackMembers: teamMembers },
      );
      if (!okToAssign) {
        setSavingAction(null);
        return;
      }
    }
    try {
      // Track the legacy single team_member_id so it stays consistent with the
      // authoritative job_assignments set as cleaners are removed/added.
      let legacyMemberId = selectedJob.team_member_id;

      for (const memberId of removedIds) {
        const { error } = await supabase
          .from('job_assignments')
          .delete()
          .eq('job_id', jobId)
          .eq('team_member_id', memberId);
        if (error) throw error;
        if (legacyMemberId === memberId) {
          legacyMemberId = effectiveDraftAssignedIds[0] ?? null;
        }
      }

      for (const memberId of addedIds) {
        const { error } = await supabase
          .from('job_assignments')
          .insert({ job_id: jobId, team_member_id: memberId, organization_id: orgId });
        if (error) throw error;
        // Keep the legacy single field populated when it was empty so older
        // reads (and any code still using team_member_id) resolve a cleaner.
        if (!legacyMemberId) legacyMemberId = memberId;
      }

      // Notes, status, and (if it moved) the legacy cleaner column commit in one
      // jobs PATCH so the three stay atomic for the calendar refresh.
      const jobUpdates: Record<string, unknown> = { notes: editableNotes || null };
      if (draftStatus !== selectedJob.status) jobUpdates.status = draftStatus;
      if (legacyMemberId !== selectedJob.team_member_id) jobUpdates.team_member_id = legacyMemberId;

      const { error: jobError } = await supabase.from('jobs').update(jobUpdates).eq('id', jobId);
      if (jobError) throw jobError;

      // Notify each newly-assigned cleaner — the same per-member, non-blocking
      // notification the immediate "Add cleaner" used, now fired once on Save.
      // The browser names which member was just added (teamMemberId) so a
      // 2-cleaner job notifies the SECOND cleaner, not the legacy primary again
      // — the edge function validates the member is assigned and resolves the
      // recipient. invoke() does NOT throw on HTTP errors (it resolves with
      // { error }), so check it explicitly and log, in addition to catching
      // network/throw failures. Email failure must not block the save UX.
      for (const memberId of addedIds) {
        try {
          const { error: notifyError } = await supabase.functions.invoke('send-job-assigned', {
            body: { recordId: jobId, organizationId: orgId, notificationType: 'assignment', teamMemberId: memberId },
          });
          if (notifyError) {
            console.error('Failed to send job assignment notification', notifyError);
          }
        } catch (notifyError) {
          console.error('Failed to send job assignment notification', notifyError);
        }
      }

      toast({ title: 'Saved', description: 'Job updated successfully' });

      // Refresh and re-point the open dialog at the fresh job so the assigned
      // cleaners, status, and notes reflect what was persisted — and re-seed the
      // drafts from that authoritative copy so the dialog stays usable.
      const list = await fetchData();
      const fresh = list.find((j) => j.id === jobId);
      if (fresh) {
        setSelectedJob(fresh);
        setDraftStatus(fresh.status);
        setDraftAssignedIds(getAssignedMemberIds(fresh));
        setEditableNotes(fresh.notes || '');
        setAddCleanerId('');
      }
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingAction(null);
    }
  };

  const openJobDialog = (job: Job) => {
    setSelectedJob(job);
    setEditableNotes(job.notes || '');
    setAddCleanerId('');
    setDraftStatus(job.status);
    setDraftAssignedIds(getAssignedMemberIds(job));
    setIsEditing(false);
    setIsViewDialogOpen(true);
  };

  // Switch the dialog into edit mode, seeding the form from the selected job.
  // Times are trimmed to HH:mm for the <input type="time"> controls.
  const startEditJob = () => {
    if (!selectedJob) return;
    setEditForm({
      title: selectedJob.title,
      client_name: selectedJob.client_name || '',
      client_email: selectedJob.client_email || '',
      client_phone: selectedJob.client_phone || '',
      address: selectedJob.address || '',
      scheduled_date: selectedJob.scheduled_date,
      start_time: selectedJob.start_time.slice(0, 5),
      end_time: selectedJob.end_time.slice(0, 5),
      service_id: selectedJob.service_id || '',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async (scope?: EditScope) => {
    if (readOnly) return;
    if (!selectedJob) return;

    if (!editForm.title || !editForm.scheduled_date || !editForm.start_time || !editForm.end_time) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const newStart = toMinutes(editForm.start_time);
    const newEnd = toMinutes(editForm.end_time);

    if (newEnd <= newStart) {
      toast({ title: 'Invalid time range', description: 'End time must be after start time.', variant: 'destructive' });
      return;
    }

    // Moving the job keeps its cleaners, so check every one of them (not just
    // the legacy primary) on the new date/time. Asked once, before the
    // repeat-clean scope question.
    if (!scope) {
      const okToMove = await confirmSchedule(
        {
          date: editForm.scheduled_date,
          start: editForm.start_time,
          end: editForm.end_time,
          memberIds: getAssignedMemberIds(selectedJob),
          excludeJobId: selectedJob.id,
        },
        { fallbackJobs: jobs, fallbackMembers: teamMembers },
      );
      if (!okToMove) return;
    }

    // A repeat-clean visit asks whether the change is for this visit only or
    // for this and all future visits before anything is written.
    if (selectedJob.series_id && !scope) {
      setScopeOpen(true);
      return;
    }

    setSavingAction('edit');
    if (scope) setScopeBusy(scope);
    try {
      if (scope === 'future') {
        const result = await updateSeriesFromVisit(selectedJob.id, visitChangesFromForm(editForm));
        invalidateRecurring(queryClient);
        toast({ title: 'Repeat clean updated', description: futureEditSummary(result) });
      } else {
        const { error } = await supabase
          .from('jobs')
          .update({
            title: editForm.title,
            client_name: editForm.client_name || null,
            client_email: editForm.client_email || null,
            client_phone: editForm.client_phone || null,
            address: editForm.address || null,
            scheduled_date: editForm.scheduled_date,
            start_time: editForm.start_time,
            end_time: editForm.end_time,
            service_id: editForm.service_id || null,
            // "This visit only": detach it so regenerating the series leaves it alone.
            ...(selectedJob.series_id ? { is_exception: true } : {}),
          })
          .eq('id', selectedJob.id);
        if (error) throw error;
        toast({ title: 'Success', description: 'Job details updated' });
      }
      setScopeOpen(false);
      setIsEditing(false);
      setIsViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingAction(null);
      setScopeBusy(null);
    }
  };

  // Checklist progress ("12/15") for the day's job cards, in one query.
  const { data: checklistProgress } = useChecklistProgress(jobs.map((job) => job.id));

  const getJobsForMember = (memberId: string) => {
    const memberJobs = memberId === 'unassigned'
      ? jobs.filter((job) => getAssignedMemberIds(job).length === 0)
      : jobs.filter((job) => getAssignedMemberIds(job).includes(memberId));

    // A status legend filter narrows each column to that status. The
    // 'unassigned' filter is handled by the column layout, not a status match.
    if (activeFilter && activeFilter !== 'unassigned') {
      return memberJobs.filter((job) => job.status === activeFilter);
    }
    return memberJobs;
  };

  // Stage an "Add cleaner" selection into the local draft — no write happens
  // until Save. The cleaner appears in the assigned list and count immediately
  // so the owner can review the full set before committing.
  const stageAddCleaner = (memberId: string) => {
    if (!memberId) return;
    setDraftAssignedIds((prev) => (prev.includes(memberId) ? prev : [...prev, memberId]));
    setAddCleanerId('');
  };

  // Stage a cleaner removal into the local draft — persisted only on Save.
  const stageRemoveCleaner = (memberId: string) => {
    setDraftAssignedIds((prev) => prev.filter((id) => id !== memberId));
  };

  const getJobPosition = (job: Job) => {
    const startHour = parseInt(job.start_time.split(':')[0]);
    const startMinute = parseInt(job.start_time.split(':')[1]);
    const endHour = parseInt(job.end_time.split(':')[0]);
    const endMinute = parseInt(job.end_time.split(':')[1]);

    const top = ((startHour - 6) * 60 + startMinute) * (60 / 60); // 60px per hour
    const height = ((endHour - startHour) * 60 + (endMinute - startMinute)) * (60 / 60);

    return { top: `${top}px`, height: `${Math.max(height, 40)}px` };
  };

  // Fan-out overlapping jobs into side-by-side lanes so clashing blocks stay
  // individually visible/clickable instead of stacking on the same column.
  // Jobs are grouped into clusters of mutual overlap; within a cluster each job
  // gets the first free lane (greedy), and every block in that cluster is sized
  // to the cluster's lane count so widths line up. Non-overlapping jobs end up
  // alone in their cluster → a single full-width lane.
  const getJobLanes = (memberJobs: Job[]) => {
    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const layout = new Map<string, { lane: number; lanes: number }>();
    const sorted = [...memberJobs].sort((a, b) =>
      toMinutes(a.start_time) - toMinutes(b.start_time) ||
      toMinutes(a.end_time) - toMinutes(b.end_time)
    );

    let cluster: Job[] = [];
    let clusterEnd = -Infinity;

    const flush = () => {
      if (cluster.length === 0) return;
      const laneEnds: number[] = [];
      const laneOf = new Map<string, number>();
      for (const job of cluster) {
        const start = toMinutes(job.start_time);
        let lane = laneEnds.findIndex((end) => end <= start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(toMinutes(job.end_time));
        } else {
          laneEnds[lane] = toMinutes(job.end_time);
        }
        laneOf.set(job.id, lane);
      }
      for (const job of cluster) {
        layout.set(job.id, { lane: laneOf.get(job.id) ?? 0, lanes: laneEnds.length });
      }
      cluster = [];
      clusterEnd = -Infinity;
    };

    for (const job of sorted) {
      if (cluster.length > 0 && toMinutes(job.start_time) >= clusterEnd) flush();
      cluster.push(job);
      clusterEnd = Math.max(clusterEnd, toMinutes(job.end_time));
    }
    flush();

    return layout;
  };

  const PX_PER_HOUR = 60;
  const gridStartMinutes = HOURS[0] * 60;
  const gridHeight = HOURS.length * PX_PER_HOUR;
  const isViewingToday = isSameDay(currentDate, now);
  const nowOffset = (now.getHours() * 60 + now.getMinutes() - gridStartMinutes) * (PX_PER_HOUR / 60);
  const showNowLine = isViewingToday && nowOffset >= 0 && nowOffset <= gridHeight;
  const dayHours = getBusinessHoursForDay(currentDate);
  // Contiguous closed ranges for the striped "Closed" blocks. Only drawn when
  // the day has business hours configured, so unconfigured orgs stay clean.
  const closedRanges: Array<{ start: number; end: number }> = [];
  if (dayHours) {
    HOURS.forEach((hour, index) => {
      if (isHourOpen(currentDate, hour)) return;
      const last = closedRanges[closedRanges.length - 1];
      if (last && last.end === index) last.end = index + 1;
      else closedRanges.push({ start: index, end: index + 1 });
    });
  }
  const totalJobsToday = jobs.filter((job) => !selectedMemberId || getAssignedMemberIds(job).includes(selectedMemberId)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Day Navigation */}
      <section className="adm-panel overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-3 md:px-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className={adminButton.icon} onClick={handlePrevDay} aria-label="Previous day">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className={cn(adminButton.outline, 'h-8')} onClick={handleToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" className={adminButton.icon} onClick={handleNextDay} aria-label="Next day">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="ml-1 leading-tight">
              <h2 className="text-[15px] font-semibold text-foreground">
                {format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
              <p className="text-[12px] text-muted-foreground">
                <span className="adm-num font-semibold text-foreground">{totalJobsToday}</span> {totalJobsToday === 1 ? 'job' : 'jobs'} on this day
              </p>
            </div>
          </div>
          {/* Static colour key only — the category buttons that browse jobs
              across dates now live in the Schedule Overview above the calendar
              (issue #49), keeping the grid a focused single-day view. Cleaners
              never see the Unassigned swatch. */}
          <div
            data-testid="dispatch-legend"
            className="flex flex-wrap items-center gap-1.5"
          >
            {STATUS_FILTERS.filter((f) => !readOnly || f.key !== 'unassigned').map((f) => (
              <StatusChip key={f.key} status={f.key}>
                {f.label}
              </StatusChip>
            ))}
          </div>
        </div>
        <div ref={scrollRef} className="adm-scroll relative max-h-[calc(100vh-230px)] min-h-[420px] overflow-auto">
          {/* Dispatch Grid - Team Members as Columns */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: `64px repeat(${displayedMembers.length}, minmax(176px, 1fr))`,
              minWidth: `${64 + displayedMembers.length * 176}px`,
            }}
          >
            {/* Header Row - Team Member Avatars */}
            <div className="sticky left-0 top-0 z-30 flex h-[64px] items-end border-b border-r border-border bg-card px-2 pb-2">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {format(currentDate, 'O')}
              </span>
            </div>
            {displayedMembers.map((member) => {
              const count = getJobsForMember(member.id).length;
              const isUnassigned = member.id === 'unassigned';
              return (
                <div
                  key={member.id}
                  className="sticky top-0 z-20 flex h-[64px] items-center gap-2.5 border-b border-r border-border bg-card px-3 last:border-r-0"
                  data-testid="dispatch-column-header"
                  data-member-id={member.id}
                >
                  <PersonAvatar name={isUnassigned ? null : member.name} seed={member.id} size={34} placeholder={isUnassigned} />
                  <div className="min-w-0 leading-tight">
                    <span className="block truncate text-[13px] font-semibold text-foreground">{member.name}</span>
                    <span className="block truncate text-[11.5px] text-muted-foreground">
                      {count} {count === 1 ? 'job' : 'jobs'}{isUnassigned ? ' to assign' : ' today'}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Time Column */}
            <div className="sticky left-0 z-10 border-r border-border bg-card" style={{ height: gridHeight }}>
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="relative pr-2 text-right text-[11px] font-medium text-muted-foreground"
                  style={{ height: PX_PER_HOUR }}
                >
                  <span className="relative -top-[7px]">
                    {format(new Date().setHours(hour, 0), 'h a').toLowerCase()}
                  </span>
                </div>
              ))}
              {showNowLine && (
                <span className="adm-now-pill right-1" style={{ top: nowOffset }}>
                  {format(now, 'HH:mm')}
                </span>
              )}
            </div>

            {/* Team Member Columns */}
            {displayedMembers.map((member, columnIndex) => {
              const memberJobs = getJobsForMember(member.id);
              const jobLanes = getJobLanes(memberJobs);

              return (
                <div
                  key={member.id}
                  className="relative border-r border-border last:border-r-0"
                  style={{ height: gridHeight }}
                  data-testid="dispatch-column"
                  data-member-id={member.id}
                >
                  {/* Hour Lines */}
                  {HOURS.map((hour) => (
                    <div key={hour} className="adm-cal-hour adm-cal-half relative" style={{ height: PX_PER_HOUR }} />
                  ))}

                  {/* Closed (outside business hours) */}
                  {closedRanges.map((range) => (
                    <div
                      key={`closed-${range.start}`}
                      className="adm-stripes pointer-events-none absolute inset-x-0 flex items-center justify-center bg-muted/30"
                      style={{ top: range.start * PX_PER_HOUR, height: (range.end - range.start) * PX_PER_HOUR }}
                      aria-hidden="true"
                    >
                      {range.end - range.start >= 2 && (
                        <span className="adm-label rounded-md bg-card/80 px-2 py-0.5 text-[9.5px]">
                          {dayHours?.is_open ? 'Closed' : 'Closed all day'}
                        </span>
                      )}
                    </div>
                  ))}

                  {showNowLine && (
                    <div className={cn('adm-now-line', columnIndex > 0 && 'adm-now-line--plain')} style={{ top: nowOffset }} aria-hidden="true" />
                  )}

                  {/* Jobs */}
                  <div className="absolute inset-0" style={{ top: 0 }}>
                    {memberJobs.map((job) => {
                      const position = getJobPosition(job);
                      const isOverlapping = overlappingJobIds.has(job.id);
                      const { lane, lanes } = jobLanes.get(job.id) ?? { lane: 0, lanes: 1 };
                      const widthPct = 100 / lanes;
                      const meta = getStatusMeta(job.status);
                      const heightPx = parseFloat(position.height);
                      const roomy = heightPx >= 64;
                      return (
                        <div
                          key={job.id}
                          data-testid={`dispatch-job-${job.id}`}
                          className={cn(
                            'adm-job absolute cursor-pointer overflow-hidden',
                            `adm-tone-${meta.tone}`,
                            job.status === 'cancelled' && 'adm-job--cancelled',
                            isOverlapping && 'ring-2 ring-destructive ring-offset-1'
                          )}
                          data-job-id={job.id}
                          data-overlapping={isOverlapping || undefined}
                          data-lane={lane}
                          data-lanes={lanes}
                          style={{
                            ...position,
                            left: `calc(${widthPct * lane}% + 4px)`,
                            width: `calc(${widthPct}% - 8px)`,
                          }}
                          onClick={() => openJobDialog(job)}
                        >
                          <div className="flex h-full flex-col gap-1 px-2.5 py-2">
                            <div className="flex min-w-0 items-start gap-1.5">
                              {isOverlapping ? (
                                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-destructive" aria-label="Overlapping schedule" />
                              ) : (
                                <span className="adm-job-accent mt-[3px] h-2.5 w-2.5 shrink-0 rounded-[3px]" aria-hidden="true" />
                              )}
                              <p className="adm-job-title min-w-0 flex-1 truncate text-[12.5px] font-semibold leading-tight">{job.client_name || job.title}</p>
                              {job.series_id && (
                                <Repeat
                                  className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                  aria-label="Repeat clean"
                                  data-testid={`dispatch-job-repeat-${job.id}`}
                                />
                              )}
                              {roomy && lanes === 1 && (
                                <StatusChip status={job.status} className="h-[18px] shrink-0 px-1.5 text-[10px]" />
                              )}
                            </div>
                            <div className="flex min-w-0 items-center gap-1.5 pl-4">
                              <p className="adm-job-ink truncate text-[11px] font-medium leading-tight">
                                {job.start_time.slice(0, 5)} - {job.end_time.slice(0, 5)}
                              </p>
                              <ChecklistProgressBadge
                                progress={checklistProgress?.get(job.id)}
                                testId={`dispatch-job-checklist-${job.id}`}
                              />
                            </div>
                            {roomy && (
                              <div className="mt-auto flex min-w-0 items-center gap-1.5 pl-4">
                                <span className="adm-job-tag">{job.services?.name || job.title}</span>
                                {job.client_phone && lanes === 1 && heightPx >= 100 && (
                                  <span className="truncate text-[10.5px] text-muted-foreground">{job.client_phone}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* View Job Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title}</DialogTitle>
            <DialogDescription>
              {selectedJob && format(parseISO(selectedJob.scheduled_date), 'EEEE, MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>
          {selectedJob && !isEditing && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  {selectedJob.start_time.slice(0, 5)} – {selectedJob.end_time.slice(0, 5)}
                </div>
                <StatusChip status={readOnly ? selectedJob.status : draftStatus}>
                  {(readOnly ? selectedJob.status : draftStatus).replace('_', ' ')}
                </StatusChip>
              </div>

              {selectedJob.address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span>{selectedJob.address}</span>
                </div>
              )}

              {selectedJob.client_name && (
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{selectedJob.client_name}</span>
                  </div>
                  {selectedJob.client_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{selectedJob.client_phone}</span>
                    </div>
                  )}
                  {selectedJob.client_email && (
                    <p className="text-muted-foreground pl-6">{selectedJob.client_email}</p>
                  )}
                </div>
              )}

              {!readOnly && (() => {
                const assignedIds = draftAssignedIds;
                const required = getRequiredCleaners(selectedJob);
                const assignedMembers = assignedIds
                  .map((id) => teamMembers.find((m) => m.id === id))
                  .filter((m): m is TeamMember => Boolean(m));
                const unassigned = teamMembers.filter((m) => !assignedIds.includes(m.id));
                const understaffed = assignedIds.length < required;

                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>Cleaners</Label>
                      <div className="flex items-center gap-2">
                        {required > 1 && (
                          <span className="text-xs font-medium text-muted-foreground">
                            Needs {required} cleaners
                          </span>
                        )}
                        <span
                          data-testid="cleaner-count"
                          className={cn(
                            'text-xs font-semibold',
                            understaffed ? 'text-destructive' : 'text-emerald-600'
                          )}
                        >
                          {assignedIds.length}/{required} assigned
                        </span>
                      </div>
                    </div>

                    {assignedMembers.length > 0 ? (
                      <ul className="space-y-1">
                        {assignedMembers.map((m) => (
                          <li
                            key={m.id}
                            className="flex items-center justify-between rounded-md border px-2 py-1 text-sm"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <PersonAvatar name={m.name} seed={m.id} size={24} />
                              <span className="truncate">{m.name}</span>
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              aria-label={`Remove ${m.name}`}
                              onClick={() => stageRemoveCleaner(m.id)}
                              disabled={isSaving}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-muted-foreground">No cleaners assigned yet.</p>
                    )}

                    {unassigned.length > 0 && (
                      <Select value={addCleanerId} onValueChange={stageAddCleaner}>
                        <SelectTrigger data-testid="add-cleaner-select" className="w-full">
                          <SelectValue placeholder="Add a cleaner" />
                        </SelectTrigger>
                        <SelectContent>
                          {unassigned.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })()}

              {!readOnly && (
                <RepeatVisitPanel
                  // Dispatch jobs are loaded with every column, so a missing
                  // series_id simply means a one-off job (no lookup needed).
                  job={{ ...selectedJob, series_id: selectedJob.series_id ?? null }}
                  canWrite={!readOnly}
                  onChanged={() => fetchData()}
                  onClose={() => setIsViewDialogOpen(false)}
                />
              )}

              {!readOnly && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={draftStatus}
                    onValueChange={setDraftStatus}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {readOnly ? (
                selectedJob.notes && (
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                      {selectedJob.notes}
                    </p>
                  </div>
                )
              ) : (
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editableNotes}
                    onChange={(e) => setEditableNotes(e.target.value)}
                    placeholder="Add notes about this job..."
                    rows={3}
                  />
                </div>
              )}

              <JobWorkPanel job={selectedJob} onChanged={() => fetchData()} />

              {readOnly ? (
                <div className="flex justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 pt-4 border-t">
                  <div className="flex min-w-0 flex-1 items-center gap-2" data-testid="job-dialog-secondary-actions">
                    <Button variant="outline" onClick={startEditJob} className="gap-2 whitespace-nowrap">
                      <Pencil className="h-4 w-4" />
                      Edit Job
                    </Button>
                    {selectedJob.status !== 'completed' && (
                      <Button
                        variant="outline"
                        onClick={handleCompleteJob}
                        disabled={isSaving}
                        className="gap-2 whitespace-nowrap"
                      >
                        {savingAction === 'complete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Complete Job
                      </Button>
                    )}
                  </div>
                  <Button
                    data-testid="job-dialog-primary-save"
                    onClick={handleSaveJob}
                    disabled={isSaving}
                    className="gap-2 whitespace-nowrap"
                  >
                    {savingAction === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save
                  </Button>
                </div>
              )}
            </div>
          )}

          {selectedJob && isEditing && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Job title"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-date">Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={editForm.scheduled_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, scheduled_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-service">Service</Label>
                  <Select
                    value={editForm.service_id || 'none'}
                    onValueChange={(v) => setEditForm((prev) => ({ ...prev, service_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger id="edit-service">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-start">Start time</Label>
                  <Input
                    id="edit-start"
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end">End time</Label>
                  <Input
                    id="edit-end"
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-client-name">Client name</Label>
                <Input
                  id="edit-client-name"
                  value={editForm.client_name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Client name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-client-phone">Phone</Label>
                  <Input
                    id="edit-client-phone"
                    value={editForm.client_phone}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="Phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-email">Email</Label>
                  <Input
                    id="edit-client-email"
                    type="email"
                    value={editForm.client_email}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, client_email: e.target.value }))}
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editForm.address}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Address"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Team member, status and notes are managed from the job details view.
              </p>

              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={() => handleSaveEdit()} disabled={isSaving} className="gap-2">
                  {savingAction === 'edit' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditScopeDialog
        open={scopeOpen}
        onOpenChange={(open) => !scopeBusy && setScopeOpen(open)}
        onChoose={(scope) => handleSaveEdit(scope)}
        futureDisabled={selectedJob?.status === 'completed' || selectedJob?.status === 'cancelled'}
        busy={scopeBusy}
      />

      <ScheduleClashDialog {...clashPrompt} />
    </div>
  );
};

export default DispatchCalendar;
